import styled from '@emotion/styled';
import React from 'react';
import { Theme } from '../../../../../../../theme';
import {
  getCancelInfoStr,
  getGoalTypeStr,
  getRateInfoStr,
  getStatusStr,
} from '../../translations';
import {
  GoalCategory,
  GoalEmployee,
  GoalRateInfo,
  GoalRelatedGoal,
  GoalStatus,
  GoalTag,
  GoalType,
  GoalVisibility,
} from '../../types';
import { GoalLabel } from './GoalLabel';
import { GoalTagLabels } from './GoalTagLabels';
import { GoalVisibilityLabel } from './GoalVisibilityLabel';
import { useGoalsContext } from '../../GoalsContext/GoalsContext';
import { useGoalViewBy } from '../../useGoalViewBy';
import { GoalRelatedLabel } from './GoalRelatedLabel';

type Props = {
  goalType: GoalType;
  rateInfo: GoalRateInfo;
  status: GoalStatus;
  periodName: string | undefined;
  visiblity: GoalVisibility;
  sharedWith?: GoalEmployee[]; // When visibility == protected
  goalId: string;
  tags: GoalTag[];
  cancelReason?: string;
  statusChangedBy?: GoalEmployee;
  category: GoalCategory | null;
  parent: GoalRelatedGoal | null;
  children: GoalRelatedGoal[];
};

// todo: In next PR. "Add Canceled by or not implement!?
export const GoalCardLabels: React.FC<Props> = ({
  goalType,
  status,
  periodName,
  goalId,
  visiblity,
  sharedWith,
  rateInfo,
  tags,
  cancelReason,
  statusChangedBy,
  category,
  parent,
  children,
}) => {
  const { developmentFrameworkEnabled } = useGoalsContext();
  // it uses local storage value, it doesn't create new state
  const [viewBy] = useGoalViewBy();
  const allLabels = status === 'BEHIND' || status === 'ON_TRACK';
  const isRatedVisible =
    rateInfo.rate && (rateInfo.rate !== 'OPEN' || status === 'DONE');
  //Canceled by John Doe (HR) due to “change on priorities”
  const isCancelVisible =
    status === 'CANCELED' && cancelReason && statusChangedBy;

  const isCategoryVisible =
    category && developmentFrameworkEnabled && viewBy !== 'category';

  const isStatusVisible = viewBy !== 'status';

  return (
    <Container>
      <GoalLabel
        icon="bullseye-outline"
        label={getGoalTypeStr(goalType)}
        className="me-3"
        noWrap={true}
      />
      {isStatusVisible && (
        <GoalLabel
          icon="phosphor-pulse"
          label={getStatusStr(status)}
          className="me-3"
        />
      )}
      <GoalRelatedLabel
        goalId={{ id: goalId, type: goalType }}
        parent={parent}
        children={children}
      />
      {isCategoryVisible && category && (
        <GoalLabel
          icon="phosphor-stack"
          label={category.name}
          className="me-3"
        />
      )}
      {isRatedVisible && (
        <GoalLabel
          icon="information-circle-filled"
          label={getRateInfoStr(rateInfo)}
          className="me-3"
        />
      )}
      {isCancelVisible && (
        <GoalLabel
          icon="information-circle-filled"
          label={getCancelInfoStr(cancelReason, statusChangedBy)}
          className="me-3"
        />
      )}

      {allLabels && (
        <>
          {!developmentFrameworkEnabled && periodName && (
            <GoalLabel
              icon="tilde-outline"
              label={periodName}
              className="me-3"
            />
          )}

          <GoalVisibilityLabel
            goalId={goalId}
            visiblity={visiblity}
            sharedWith={sharedWith}
          />
          <GoalTagLabels goalId={goalId} tags={tags} />
        </>
      )}
    </Container>
  );
};

const Container = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  color: Theme.color.dark,
  fontSize: Theme.font.size.sm,
});
